import type { ReactNode } from 'react';
import { Box, NavLink as MantineNavLink, ScrollArea } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Link, useLocation } from 'react-router-dom';

/** One destination in a navigation list. */
export interface Section {
  readonly to: string;
  readonly label: string;
  readonly icon?: ReactNode;
  /** True when only the exact address counts as being here. */
  readonly end?: boolean;
}

/**
 * Below this the rail lies down and becomes a row.
 *
 * Asked as a max-width rather than a min-width so that "no answer" means the wide layout. jsdom
 * answers every media query with false, so tests see the rail people actually use rather than the
 * narrow fallback.
 */
const NARROW = '(max-width: 62em)';

/** True on a window too narrow for a rail beside the content. */
export function useNarrowLayout(): boolean {
  return useMediaQuery(NARROW, false) ?? false;
}

/** Whether the current address counts as being in a section. */
export function inSection(pathname: string, to: string, end = false): boolean {
  if (pathname === to) return true;
  return !end && pathname.startsWith(`${to}/`);
}

/**
 * A list of sections, drawn down the side on a wide window and across the top on a narrow one.
 *
 * Both shapes render one nav element with one link per section, never two copies hidden from each
 * other by CSS: a second copy would be a second link with the same name, which is a nuisance for
 * anybody reading the page with a screen reader and a trap for anybody testing it.
 */
export function SectionNav({
  label,
  sections,
  orientation,
}: {
  /** What this navigation is called, for anyone who cannot see where it sits. */
  label: string;
  sections: readonly Section[];
  orientation: 'vertical' | 'horizontal';
}) {
  const { pathname } = useLocation();
  const vertical = orientation === 'vertical';

  const links = sections.map((section) => (
    <MantineNavLink
      key={section.to}
      component={Link}
      to={section.to}
      label={section.label}
      leftSection={section.icon}
      active={inSection(pathname, section.to, section.end)}
      aria-current={inSection(pathname, section.to, section.end) ? 'page' : undefined}
      variant="light"
      color="sage"
      className="quizee-nav-item"
      style={{ flex: vertical ? undefined : '0 0 auto', width: vertical ? undefined : 'auto' }}
    />
  ));

  if (vertical) {
    return (
      <Box component="nav" aria-label={label}>
        {links}
      </Box>
    );
  }

  return (
    <ScrollArea type="never" mb="md">
      <Box
        component="nav"
        aria-label={label}
        className="quizee-horizontal-nav"
        style={{ display: 'flex', gap: 4, flexWrap: 'nowrap' }}
      >
        {links}
      </Box>
    </ScrollArea>
  );
}
